<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const logger = useLogger("Container");

/** 1) Expect 'id' from the parent */
const props = defineProps<{ id: string }>();

/** 2) Get access to the store's read method only */
const { getContentById } = useContentManager();

/**
 * 3) Read-only computed property wrapping the container definition data.
 */
const definitionData = computed(() => getContentById(props.id));

/** 4) Compute the grid classes based on the container's columnsSettings */
const classes = computed(() => {
  // Fallback to 1 column if data or columnsSettings is not defined.
  const settings = definitionData.value?.columnsSettings ?? { default: 1 };
  let classList = "grid";
  if (settings.default) classList += ` grid-cols-${settings.default}`;
  return classList;
});


/** 6) Commands for context menu (optional) */
const commands = [
  {
    id: "editors-group",
    name: "Container",
    description: "Configure this container",
    type: "group",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Number of columns",
        action: async () => {
          logger.info("Edit command triggered for container", props.id);
          logger.debug("Emitting event for onEdit with container data", {
            data: definitionData.value,
          });
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 7) Expose commands & properties for the resource editor / context menu */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Container",
    icon: "lucide:box-select",
  },
});
</script>

<template>
  <!-- The main container layout -->
  <div :class="cn(classes, $attrs.class ?? '')">
    <!-- We assume the parent uses <Renderer> to insert child blocks in the slot -->
    <slot />
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.CONTAINER,
  editor: () => import("#bb/components/editor/container/index.vue"),
  id: "container",
  icon: "lucide:box-select",
  type: "Container",
  description:
    "A flexible container that arranges its child building blocks using responsive grid layouts.",
  default: {
    columnsSettings: {
      default: 1,
    },
  },
  categories: ["Containers"],
  order: 12004,
};
</script>
